/**
 * Utilidades generales para el sitio web de Fuigo
 */

// Detección de dispositivo
export const DeviceDetector = {
  isMobile: () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
  isIOS: () => /iPad|iPhone|iPod/.test(navigator.userAgent),
  isAndroid: () => /android/i.test(navigator.userAgent),
  isTouchDevice: () => 'ontouchstart' in window || navigator.maxTouchPoints > 0
};

// Gestión de errores
export const ErrorHandler = {
  display: (message, type = 'error') => {
    const container = document.getElementById('error-container') || createErrorContainer();
    const errorDiv = document.createElement('div');
    errorDiv.className = `${type}-message animate-slide-up`;
    errorDiv.setAttribute('role', 'alert');
    errorDiv.setAttribute('aria-live', 'polite');
    
    const icon = getIconForType(type);
    errorDiv.innerHTML = `
      <span aria-hidden="true">${icon}</span>
      <span>${message}</span>
      <button 
        class="ml-auto text-current hover:opacity-70 transition-opacity" 
        onclick="this.parentElement.remove()"
        aria-label="Cerrar mensaje"
      >
        ✕
      </button>
    `;
    
    container.appendChild(errorDiv);
    
    // Auto-remover después de 5 segundos
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  },
  
  log: (error) => {
    console.error('[Fuigo Error]:', error);
    // Aquí se podría integrar con un servicio de logging como Sentry
  }
};

function createErrorContainer() {
  const container = document.createElement('div');
  container.id = 'error-container';
  container.className = 'fixed top-4 right-4 z-50 space-y-2 max-w-md';
  document.body.appendChild(container);
  return container;
}

function getIconForType(type) {
  const icons = {
    error: '❌',
    success: '✅',
    info: 'ℹ️',
    warning: '⚠️'
  };
  return icons[type] || icons.info;
}

// Rate Limiter para prevenir abuso de API
export class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }
  
  canMakeRequest() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    
    if (this.requests.length >= this.maxRequests) {
      return false;
    }
    
    this.requests.push(now);
    return true;
  }
  
  getRemainingRequests() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    return this.maxRequests - this.requests.length;
  }
}

// Sanitización de inputs
export function sanitizeInput(input) {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[<>\"']/g, '') // Remover caracteres peligrosos
    .trim()
    .substring(0, 200); // Limitar longitud
}

// Validación de dirección
export function validateAddress(address) {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  if (trimmed.length < 3) return false;
  
  // Validar formato de dirección (letras, números, espacios, comas, puntos, guiones, #, etc.)
  const pattern = /^[a-zA-Z0-9\s,.\-#áéíóúñÁÉÍÓÚÑüÜ()°/]+$/;
  return pattern.test(trimmed);
}

// MediaManager para lazy loading
export const MediaManager = {
  lazyLoad: (selector = 'img[loading="lazy"], video[loading="lazy"]') => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target;
            
            if (element.tagName === 'IMG') {
              if (element.dataset.src) {
                element.src = element.dataset.src;
              }
            } else if (element.tagName === 'VIDEO') {
              if (element.dataset.src) {
                element.src = element.dataset.src;
              }
              element.load();
            }
            
            element.classList.remove('lazy-loading');
            element.classList.add('lazy-loaded');
            observer.unobserve(element);
          }
        });
      }, {
        rootMargin: '50px'
      });
      
      document.querySelectorAll(selector).forEach(element => {
        element.classList.add('lazy-loading');
        observer.observe(element);
      });
    }
  },
  
  addPlaceholder: (element) => {
    element.classList.add('image-placeholder');
  },
  
  removePlaceholder: (element) => {
    element.classList.remove('image-placeholder');
  }
};

// Formateo de precios
export function formatPrice(price) {
  if (typeof price !== 'number') return 'N/A';
  
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

// Debounce para optimizar eventos
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Analytics (opcional)
export const Analytics = {
  trackSearch: (origin, destination) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'search', {
        'search_term': `${origin} to ${destination}`,
        'event_category': 'simulator',
        'event_label': 'price_comparison'
      });
    }
  },
  
  trackClick: (service, price) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'select_content', {
        'content_type': 'transport_service',
        'content_id': service,
        'value': price
      });
    }
  },
  
  trackError: (errorType, errorMessage) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'exception', {
        'description': errorMessage,
        'fatal': false
      });
    }
  }
};

// Manejo de errores globales
window.addEventListener('error', (event) => {
  ErrorHandler.log({
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  });
});

window.addEventListener('unhandledrejection', (event) => {
  ErrorHandler.log({
    type: 'unhandledrejection',
    reason: event.reason
  });
});
